import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, History, Clock, Database, Globe, Activity, Send, Check, X, FileSpreadsheet, Link2, Download, Save, RotateCw, LayoutGrid, ChevronDown, AlignLeft } from 'lucide-react';

// Exact AutonomousDiligenceWorkflow (FreightFlow) animation, fitted into the standard product box size.
export const ChatWorkflow = () => {
  // Animation Stages:
  // 0: Initial · 2: User asks · 3: Agent typing · 4: Agent answers · 5: Applying · 6: Applied · 7: Doc updates · 8: Reveal
  const [stage, setStage] = useState(0);
  const messagesEndRef = useRef(null);
  const highlightRef = useRef(null);
  const feedRef = useRef(null);
  const mainRef = useRef(null);
  const boxRef = useRef(null);
  const [scale, setScale] = useState(0.48);

  // Orchestrate the timeline (exact), looped
  useEffect(() => {
    const timeline = [
      { delay: 1500, nextStage: 2 },  // User message
      { delay: 2500, nextStage: 3 },  // Agent typing
      { delay: 5500, nextStage: 4 },  // Agent response & suggested edit
      { delay: 8500, nextStage: 5 },  // Applying edit
      { delay: 9300, nextStage: 6 },  // Applied
      { delay: 10300, nextStage: 7 }, // Document updates & highlights
      { delay: 13500, nextStage: 8 }, // Reveal
    ];
    let timeouts = [];
    const run = () => {
      timeouts = timeline.map(({ delay, nextStage }) => setTimeout(() => setStage(nextStage), delay));
      timeouts.push(setTimeout(() => { setStage(0); run(); }, 17000));
    };
    run();
    return () => timeouts.forEach(clearTimeout);
  }, []);

  // Auto-scroll chat (panel-local so the page doesn't jump)
  useEffect(() => {
    if (stage >= 2 && feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [stage]);

  // Scroll the document pane to reveal the applied change (panel-local)
  useEffect(() => {
    if (stage === 7 && mainRef.current) mainRef.current.scrollTop = mainRef.current.scrollHeight;
    if (stage === 0 && mainRef.current) mainRef.current.scrollTop = 0;
  }, [stage]);

  // Inject Fonts (Inter & JetBrains Mono)
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  // Fit the fixed 1280×720 stage to the box width
  useEffect(() => {
    const measure = () => {
      if (boxRef.current) {
        const { offsetWidth: w, offsetHeight: h } = boxRef.current;
        // Fit the whole 1280×720 app inside the box on both axes
        setScale(Math.min(w / 1280, h / 720));
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <div
      ref={boxRef}
      className="relative w-full max-w-[620px] h-[340px] sm:h-[380px] rounded-2xl overflow-hidden border border-stone-200 bg-[#F3F4F6] flex items-center justify-center"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Scaled stage, centered */}
      <div style={{ width: 1280 * scale, height: 720 * scale, position: 'relative' }}>
        <div style={{ width: 1280, height: 720, transform: `scale(${scale})`, transformOrigin: 'top left', position: 'absolute', top: 0, left: 0 }}>

        {/* Main App Container */}
        <div className="w-[1280px] h-[720px] bg-[#FBFBFB] rounded-xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">

          {/* TOP HEADER */}
          <header className="h-14 border-b border-slate-200 flex items-center justify-between px-4 shrink-0 bg-white z-20">
            <div className="flex items-center gap-4">
              <button className="text-slate-500 hover:text-[#090E1A] flex items-center gap-1 text-sm font-medium transition-colors">
                ← Back
              </button>
              <div className="w-px h-4 bg-slate-200"></div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-[#F58220] flex items-center justify-center text-white">
                  <Activity size={14} strokeWidth={2.5} />
                </div>
                <h1 className="font-bold text-[#090E1A] text-sm">Team Research Studio</h1>
                <span className="text-slate-400 text-sm mx-1">—</span>
                <span className="text-slate-500 text-sm">FreightFlow — Market Research & Competitive Analysis</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
                <RotateCw size={14} /> Re-run
              </button>
              <button className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
                <Download size={14} /> Download
              </button>
              <button className="flex items-center gap-1.5 px-4 py-1.5 bg-[#090E1A] text-white rounded text-sm font-medium hover:bg-slate-800 shadow-sm transition-colors">
                <Save size={14} /> Save
              </button>
            </div>
          </header>

          {/* BODY */}
          <div className="flex flex-1 overflow-hidden">

            {/* LEFT SIDEBAR (Outline) */}
            <aside className="w-64 border-r border-slate-200 bg-[#FBFBFB] flex flex-col shrink-0 overflow-y-auto hidden md:flex">
              <div className="p-4 flex items-center justify-between text-[11px] font-mono font-semibold text-slate-500 tracking-[0.15em] uppercase" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                OUTLINE
                <div className="flex gap-2">
                  <span className="cursor-pointer hover:text-slate-800 text-lg leading-none">+</span>
                  <LayoutGrid size={14} className="cursor-pointer hover:text-slate-800" />
                </div>
              </div>

              <nav className="flex flex-col text-sm">
                <div className="px-4 py-2.5 bg-amber-50 text-[#F58220] font-bold border-l-[3px] border-[#F58220] text-[13px]">
                  FREIGHTFLOW — ANALYST RE...
                </div>
                <div className="px-4 py-2 font-bold text-[#090E1A] hover:bg-slate-100 cursor-pointer text-[13px]">
                  EXECUTIVE SUMMARY
                </div>
                <div className="px-4 py-2 font-bold text-[#090E1A] hover:bg-slate-100 cursor-pointer flex items-center justify-between text-[13px]">
                  SECTION 1: MARKET RESEA...
                  <ChevronDown size={14} className="text-slate-400" />
                </div>
                <div className="px-4 py-1.5 pl-8 text-slate-600 hover:bg-slate-100 cursor-pointer text-[13px] font-medium">
                  1.1 Market Size & Growth Proj...
                </div>
                <div className="px-4 py-1.5 pl-12 text-slate-500 hover:bg-slate-100 cursor-pointer text-[13px]">
                  Global Digital Freight M...
                </div>
                <div className="px-4 py-1.5 pl-12 text-slate-500 hover:bg-slate-100 cursor-pointer text-[13px]">
                  U.S. Routing Algorithms...
                </div>
                <div className="px-4 py-1.5 pl-8 text-slate-600 hover:bg-slate-100 cursor-pointer text-[13px] font-medium">
                  1.2 Key Market Trends
                </div>
                <div className="px-4 py-1.5 pl-12 text-slate-500 hover:bg-slate-100 cursor-pointer text-[13px]">
                  Trend #1: Capacity Opti...
                </div>
                <div className="px-4 py-1.5 pl-12 text-slate-500 hover:bg-slate-100 cursor-pointer text-[13px]">
                  Trend #2: Green Logist...
                </div>

                <div className="px-4 py-2 mt-2 font-bold text-[#090E1A] hover:bg-slate-100 cursor-pointer flex items-center justify-between text-[13px]">
                  SECTION 2: COMPETITIVE ...
                  <ChevronDown size={14} className="text-slate-400" />
                </div>
                <div className="px-4 py-1.5 pl-8 text-slate-600 hover:bg-slate-100 cursor-pointer text-[13px] font-medium">
                  2.1 Competitive Landscape O...
                </div>
                <div className="px-4 py-1.5 pl-8 text-slate-600 hover:bg-slate-100 cursor-pointer text-[13px] font-medium">
                  2.2 Detailed Competitor Profi...
                </div>
                <div className="px-4 py-1.5 pl-12 text-slate-500 hover:bg-slate-100 cursor-pointer text-[13px]">
                  Convoy — HIGH T...
                </div>
              </nav>
            </aside>

            {/* MAIN DOCUMENT EDITOR */}
            <main ref={mainRef} className="flex-1 bg-white overflow-y-auto px-16 py-12 relative shadow-inner">
              <div className="max-w-3xl mx-auto flex flex-col gap-6 relative">

                <h1 className="text-[28px] font-black text-[#090E1A] tracking-tight uppercase">
                  I FREIGHTFLOW — ANALYST RESEARCH BRIEF
                </h1>

                <div className="text-sm text-slate-500 space-y-1 font-medium font-mono tracking-tight" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  <p>Prepared by: Investment Team | Date: 2026-10-15 | Version v1</p>
                  <p>Subject: Market Research & Competitive Analysis</p>
                </div>

                <div className="w-full h-px bg-slate-200 my-4"></div>

                <h2 className="text-[22px] font-bold text-[#090E1A] tracking-tight uppercase">
                  I EXECUTIVE SUMMARY
                </h2>

                <div className="space-y-3">
                  <h3 className="font-bold text-[#090E1A] text-[15px]">Company Overview</h3>
                  <p className="text-[14px] text-slate-700 leading-relaxed text-justify">
                    FreightFlow is a specialized B2B logistics platform that operates an AI-powered freight routing and capacity marketplace. Founded in 2022, the company aims to make global supply chains more efficient and environmentally sustainable by optimizing truckload matching and reducing empty miles. FreightFlow's predictive engine involves a comprehensive ingestion of carrier data, real-time weather, and market rates to ensure reliability, allowing shippers to purchase verified capacity at lower dynamic prices while contributing to a lower carbon footprint across the logistics sector.
                  </p>
                  <p className="text-[12px] font-mono text-slate-400" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    Company profile sources: [1][2][3][4]
                  </p>
                </div>

                <div className="space-y-3 mt-2">
                  <h3 className="font-bold text-[#090E1A] text-[15px]">Company Snapshot:</h3>
                  <ul className="text-[14px] text-slate-700 space-y-2 pl-4">
                    <li><strong>Sector:</strong> AI-powered Freight & Logistics</li>
                    <li><strong>Stage:</strong> Series B</li>
                    <li><strong>Founded:</strong> 2022</li>
                    <li><strong>HQ:</strong> Chicago, Illinois</li>
                    <li><strong>Business model:</strong> FreightFlow operates a transactional marketplace taking a take-rate on matched freight.</li>
                    <li><strong>Funding:</strong> $24.5M to date (Seed Investment + Series A Investment)</li>
                    <li><strong>Revenue / ARR:</strong> $36.55M (as of January 2025)</li>
                    <li><strong>Employees:</strong> 112 (as of December 2024)</li>
                  </ul>
                </div>

                <div className="space-y-3 mt-2">
                  <h3 className="font-bold text-[#090E1A] text-[15px]">Assessment</h3>
                  <p className="text-[14px] text-slate-700 leading-relaxed text-justify relative">
                    FreightFlow operates in the rapidly growing specialized digital freight marketplace, with a current market size of{' '}

                    {/* DYNAMIC TEXT UPDATE TARGET */}
                    <span
                      ref={highlightRef}
                      className={`relative transition-all duration-700 ${
                        stage >= 7
                          ? 'bg-[#10B981]/20 text-[#065F46] font-extrabold shadow-sm px-1.5 py-0.5 rounded border border-[#10B981]/40'
                          : 'bg-transparent text-slate-700'
                      }`}
                    >
                      {stage >= 7 ? 'SAM $12B · TAM $124.6B' : '$124.61 billion'}

                      {/* "APPLIED" Badge that pops up in the document */}
                      {stage >= 7 && (
                        <span
                          className="absolute -top-4 -right-4 flex items-center gap-1 bg-[#10B981] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm shadow-md animate-in zoom-in duration-300 font-mono tracking-wider z-10"
                          style={{ fontFamily: "'JetBrains Mono', monospace" }}
                        >
                          <Check size={10} strokeWidth={3.5} /> APPLIED
                        </span>
                      )}
                    </span>
                    {' '}projected to reach $487.47 billion by 2026, reflecting a robust CAGR of 13.2%. With an ARR of $36.55 million and a YoY growth rate of 3.6x, FreightFlow is positioned favorably against incumbent brokerages and digital competitors.
                  </p>
                </div>

              </div>
            </main>

            {/* DALLY CHAT PANEL (Right Sidebar) */}
            <aside className="w-[400px] border-l border-slate-200 bg-white flex flex-col shrink-0 z-10 shadow-[-10px_0_30px_-15px_rgba(15,23,42,0.05)]">

              {/* Chat Tabs */}
              <div className="flex border-b border-slate-200 shrink-0 bg-[#FBFBFB]">
                <button className="flex-1 flex items-center justify-center gap-2 py-3.5 text-[13px] font-bold text-[#F58220] border-b-[3px] border-[#F58220] bg-amber-50/50">
                  <Sparkles size={16} />
                  Dally
                </button>
                <button className="flex-1 flex items-center justify-center gap-2 py-3.5 text-[13px] font-semibold text-slate-500 hover:text-[#090E1A] transition-colors border-b-[3px] border-transparent">
                  <History size={16} />
                  Changes
                </button>
                <button className="flex-1 flex items-center justify-center gap-2 py-3.5 text-[13px] font-semibold text-slate-500 hover:text-[#090E1A] transition-colors border-b-[3px] border-transparent">
                  <Clock size={16} />
                  Log
                </button>
              </div>

              {/* Chat Header */}
              <div className="px-5 pt-5 pb-4 border-b border-slate-100 shrink-0">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#F58220] font-bold font-mono mb-1.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  Dally &middot; Editing
                </div>
                <h2 className="text-[14px] font-bold text-[#090E1A]">
                  FreightFlow — Analyst Research Brief
                </h2>
              </div>

              {/* Message Feed */}
              <div ref={feedRef} className="flex-1 overflow-y-auto px-5 py-6 flex flex-col gap-6 bg-[#FBFBFB]">

                {/* System Intro */}
                <div className="flex flex-col items-start animate-in fade-in duration-500">
                  <div className="text-[13px] leading-relaxed text-slate-600 bg-white border border-slate-200 p-4 rounded-xl shadow-sm font-medium">
                    Dally reads the <strong>whole document</strong> plus the data room / web. Ask for a change, keep refining across turns — the conversation is <strong>saved</strong> — and <strong>Accept</strong> when you're happy.
                  </div>
                </div>

                {/* User Message */}
                {stage >= 2 && (
                  <div className="flex flex-col items-end gap-1.5 max-w-[90%] self-end animate-in slide-in-from-bottom-2 fade-in duration-300">
                    <div className="text-[10px] uppercase tracking-[0.1em] text-slate-400 font-mono font-semibold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      Deal Analyst
                    </div>
                    <div className="text-[14px] text-white px-4 py-2.5 rounded-2xl rounded-tr-sm shadow-md bg-[#090E1A] font-medium leading-relaxed">
                      Why is the market size 10x the deck?
                    </div>
                  </div>
                )}

                {/* Typing Indicator */}
                {stage === 3 && (
                  <div className="flex items-center gap-2 animate-in fade-in duration-300">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center bg-amber-100 text-[#F58220]">
                      <Sparkles size={14} />
                    </div>
                    <div className="flex gap-1.5 bg-white shadow-sm px-3.5 py-2.5 rounded-full border border-slate-200">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}

                {/* Agent Response */}
                {stage >= 4 && (
                  <div className="flex flex-col gap-3 w-full animate-in slide-in-from-bottom-2 fade-in duration-400">
                    <div className="flex items-center gap-2 mb-[-4px]">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center bg-amber-100 text-[#F58220]">
                        <Sparkles size={14} />
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.1em] text-slate-400 font-mono font-semibold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        Dally
                      </span>
                    </div>

                    <div className="text-[14px] leading-relaxed text-[#090E1A] font-medium">
                      The deck sizes DACH only. Outside-in research covers EU-5 and GCC. Suggest framing $12B as SAM and adding it to Founder Q1.
                    </div>

                    {/* Sources / References Pills */}
                    <div className="flex flex-wrap gap-2 mt-1">
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200/80 rounded-[4px] text-[10px] font-mono font-semibold text-[#D97706] tracking-tight" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        <FileSpreadsheet size={12} strokeWidth={2.5} /> BP_v15.xlsx
                      </div>
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-[4px] text-[10px] font-mono font-semibold text-slate-500 tracking-tight" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        <Link2 size={12} strokeWidth={2.5} /> Ref [5] - external
                      </div>
                    </div>

                    {/* Suggested Edit Card */}
                    <div
                      className={`mt-2 border rounded-xl overflow-hidden transition-all duration-500 shadow-sm ${
                        stage >= 6
                          ? 'border-emerald-200 bg-emerald-50/40'
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-slate-100 bg-slate-50">
                        <span className="text-[10px] font-mono font-bold uppercase tracking-[0.1em] text-slate-500 flex items-center gap-1.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                          <AlignLeft size={12} /> Suggested Edit
                        </span>

                        {stage === 4 && (
                          <div className="flex gap-2">
                            <button className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-colors">
                              <X size={14} strokeWidth={2.5} />
                            </button>
                            <button
                              className="px-3.5 py-1 rounded-md text-[13px] font-bold text-white shadow-sm transition-transform active:scale-95 flex items-center gap-1 bg-[#F58220] hover:bg-[#EA580C]"
                            >
                              Apply
                            </button>
                          </div>
                        )}
                        {stage === 5 && (
                          <div className="px-3 py-1 text-[13px] font-bold text-[#F58220] flex items-center gap-2">
                            <div className="w-3.5 h-3.5 border-2 border-amber-200 border-t-[#F58220] rounded-full animate-spin" />
                            Applying...
                          </div>
                        )}
                        {stage >= 6 && (
                          <div className="px-2.5 py-1 rounded-[4px] text-[11px] font-black tracking-wide text-emerald-700 bg-emerald-100 flex items-center gap-1.5 uppercase">
                            <Check size={12} strokeWidth={3.5} /> Applied
                          </div>
                        )}
                      </div>

                      <div className="p-4 text-[13px] font-medium font-mono flex flex-col gap-2.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        <div className="text-slate-400 line-through decoration-red-400/60">
                          TAM $124.61 billion
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-emerald-500 mt-0.5 font-bold">→</span>
                          <span className="text-[#090E1A] font-bold bg-amber-50/50">SAM $12B · TAM $124.6B</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* System Confirm Note */}
                {stage >= 7 && (
                  <div className="flex flex-col items-start animate-in fade-in slide-in-from-bottom-2 duration-300 mt-2">
                    <div className="text-[12px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3.5 py-2.5 rounded-lg flex items-center gap-2 shadow-sm">
                      <Check size={16} className="text-emerald-600" strokeWidth={2.5} />
                      Edit successfully applied to document.
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} className="h-4" />
              </div>

              {/* Input Box Footer */}
              <div className="p-4 bg-white border-t border-slate-200 shrink-0">
                <div className="flex gap-2 mb-3">
                  <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-200/80 text-[11px] font-semibold text-[#D97706] bg-amber-50 whitespace-nowrap transition-colors hover:bg-amber-100">
                    <Database size={12} strokeWidth={2.5} /> Data room
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-200/80 text-[11px] font-semibold text-[#D97706] bg-amber-50 whitespace-nowrap transition-colors hover:bg-amber-100">
                    <Globe size={12} strokeWidth={2.5} /> Web
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 text-[11px] font-semibold text-slate-500 bg-white whitespace-nowrap transition-colors hover:bg-slate-50">
                    <Activity size={12} strokeWidth={2.5} /> Harmonic
                  </button>
                </div>

                <div className="relative flex items-center shadow-sm">
                  <input
                    type="text"
                    placeholder="Ask, argue, or explore..."
                    disabled
                    className="w-full bg-[#FBFBFB] border border-slate-200 rounded-xl py-3 pl-4 pr-12 text-[14px] font-medium text-[#090E1A] focus:outline-none placeholder:text-slate-400 focus:border-[#F58220] transition-colors"
                  />
                  <button
                    disabled
                    className="absolute right-2 w-8 h-8 rounded-lg flex items-center justify-center bg-[#F58220] text-white shadow-sm opacity-50"
                  >
                    <Send size={14} strokeWidth={2.5} className="ml-0.5" />
                  </button>
                </div>
              </div>

            </aside>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};
